import React from "react";
import { useOutletContext } from "react-router-dom";

/**
 * Lead-binder scope descriptor supplied to the reused Business Policy tab
 * components when they are rendered inside the Manage Lead Binder tab. The
 * presence of a lead scope re-keys those components' fetch/persist operations
 * from `customer_policy_id` to `lead_id`.
 */
export interface ILeadBinderScope {
  leadId: string;
  customerId?: string | number | null;
}

/**
 * The context defaults to `null` so that `useLeadBinderScope` can be called
 * from a reused Business Policy component in either the customer-policy context
 * (no provider -> `null`) or the lead-binder context (provider present).
 */
const LeadBinderScopeContext = React.createContext<ILeadBinderScope | null>(
  null,
);

/**
 * Provides the lead-scoped identifiers `{ leadId, customerId }` to descendant
 * Business Policy tab components rendered inside the Lead Binder tab.
 */
export const LeadBinderScopeProvider: React.FC<{
  value: ILeadBinderScope;
  children: React.ReactNode;
}> = (props) => {
  return (
    <LeadBinderScopeContext.Provider value={props.value}>
      {props.children}
    </LeadBinderScopeContext.Provider>
  );
};

/**
 * Returns the current lead-binder scope, or `null` when rendered outside a
 * lead binder (e.g. in the customer-policy context).
 */
export const useLeadBinderScope = (): ILeadBinderScope | null =>
  React.useContext(LeadBinderScopeContext);

/**
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the component is not rendered under a React Router `<Outlet>`
 * (which is the case inside the Lead Binder tab, since that panel is not a
 * route). The outlet context provided by `BusinessPolicyLayout` is the tuple
 * `[customerId, customerPolicyId]`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

/**
 * Resolved scope returned by {@link useBinderScope}. `customerPolicyId` is
 * `undefined` in lead scope and `leadId` is `undefined` in customer-policy
 * scope, so a consumer can branch on which identifier is present.
 *
 * `customerId` / `customerPolicyId` are typed as `number | undefined` to match
 * how the reused Business Policy components consume them (they previously read
 * these from the loosely-typed router outlet tuple). In lead scope
 * `customerPolicyId` is always `undefined`.
 */
export interface IResolvedBinderScope {
  customerId: number | undefined;
  customerPolicyId: number | undefined;
  leadId?: string;
}

/**
 * Compatibility hook consumed by the reused Business Policy tab components that
 * previously read `[customerId, customerPolicyId]` from the router outlet.
 *
 * - When a lead-binder scope is present, it wins: the component receives
 *   `{ customerId, customerPolicyId: undefined, leadId }` so it re-keys by
 *   `lead_id`.
 * - Otherwise it falls back to the router outlet context, preserving the
 *   original customer-policy behavior byte-for-byte.
 */
export function useBinderScope(): IResolvedBinderScope {
  const leadScope = useLeadBinderScope();
  const outlet = useOutletContextSafe();

  if (leadScope) {
    return {
      customerId:
        leadScope.customerId == null
          ? undefined
          : Number(leadScope.customerId),
      customerPolicyId: undefined,
      leadId: leadScope.leadId,
    };
  }

  return {
    customerId: outlet?.[0] as number | undefined,
    customerPolicyId: outlet?.[1] as number | undefined,
    leadId: undefined,
  };
}

/**
 * Pure predicate that decides whether the Business Insurance Binder sub-tabs
 * should be visible for a given lead `insurance_type` value.
 *
 * The comparison is exact and case-sensitive against the string
 * `"Business Insurance"`: no trimming of surrounding whitespace, no other field
 * is consulted. Any non-matching value (including `null`, `undefined`, `""`,
 * `"business insurance"`, `" Business Insurance "`) yields `false`.
 */
export function isBusinessBinderVisible(insuranceType: unknown): boolean {
  return insuranceType === "Business Insurance";
}
